import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Globe, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { liveProducts } from "@/data/products";
import {
  GET_STARTED_URL,
  LOGIN_URL,
  REQUEST_DEMO_URL,
  languages,
  mainNav,
  type NavGroup,
  type NavLinkItem,
} from "@/data/site";

// Anything outside the marketing app (login, the workspace) needs a full page load.
const isAppUrl = (href: string) => /^\/(login|platform|home|api)\b/.test(href) || /^https?:/.test(href);

/**
 * Signed in, "Dashboard" could only mean one product. With two, the button
 * opens the list instead: every product, with its own mark, in one place.
 */
const OpenAppMenu = () => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="cm-btn-primary h-9 gap-1.5 px-3 text-[14px]"
      >
        Open app
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-full z-50 mt-2 w-[268px] rounded-xl border border-border bg-background p-1.5 shadow-[0_20px_45px_-25px_hsl(0_0%_0%/0.5)]"
          >
            <p className="px-2.5 pb-1 pt-1.5 text-[11.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
              Your apps
            </p>
            {liveProducts.map((p) => (
              <a
                key={p.slug}
                role="menuitem"
                href={p.appUrl}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-muted"
              >
                <img src={p.logo} alt="" width={26} height={26} className="h-[26px] w-[26px]" />
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-foreground">{p.name}</span>
                  <span className="block text-[12.5px] leading-snug text-muted-foreground">{p.shortLine}</span>
                </span>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const SmartLink = ({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}) =>
  isAppUrl(href) ? (
    <a href={href} className={className} onClick={onClick}>
      {children}
    </a>
  ) : (
    <Link to={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );

const MenuColumn = ({ heading, links, onNavigate }: { heading: string; links: NavLinkItem[]; onNavigate: () => void }) => (
  <div className="min-w-[220px]">
    <p className="px-2 pb-2 text-xs font-medium text-muted-foreground">{heading}</p>
    <ul className="space-y-0.5">
      {links.map((l) => (
        <li key={l.href + l.label}>
          <SmartLink
            href={l.href}
            onClick={onNavigate}
            className={`rounded-md px-2 py-1.5 transition-colors hover:bg-muted ${l.icon ? "flex items-start gap-2.5" : "block"}`}
          >
            {l.icon && <img src={l.icon} alt="" width={28} height={28} className="mt-0.5 h-7 w-7 shrink-0" />}
            <span className="block min-w-0">
              <span className="block text-[14px] font-medium text-foreground">{l.label}</span>
              {l.description && <span className="block text-[13px] leading-snug text-muted-foreground">{l.description}</span>}
            </span>
          </SmartLink>
        </li>
      ))}
    </ul>
  </div>
);

export const LanguageMenu = ({ pill = false }: { pill?: boolean }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const translate = (code: string) => {
    setOpen(false);
    if (code === "en") return;
    window.location.href = `https://translate.google.com/translate?sl=en&tl=${code}&u=${encodeURIComponent(window.location.href)}`;
  };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Change language"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={pill ? "inline-flex h-9 items-center gap-2 rounded-full border border-border px-3.5 text-[14px] text-foreground transition-colors hover:bg-muted" : "cm-btn-ghost h-9 px-2.5 text-[14px] text-muted-foreground"}
      >
        <Globe className="h-4 w-4" />
        <span className={pill ? "" : "hidden xl:inline"}>English</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.14 }}
            className={`absolute z-50 w-52 ${pill ? "bottom-full left-0 mb-2 lg:left-auto lg:right-0" : "right-0 top-full mt-2"} rounded-xl border border-border bg-popover p-1.5 shadow-[var(--shadow-card)]`}
          >
            {languages.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => translate(l.code)}
                className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[14px] text-foreground hover:bg-muted"
              >
                {l.label}
                {l.code === "en" && <span className="text-xs text-muted-foreground">Current</span>}
              </button>
            ))}
            <p className="border-t border-border px-2.5 pb-1 pt-2 text-xs leading-snug text-muted-foreground">
              Other languages open an automatic translation.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { pathname } = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close everything on navigation.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Signed-in visitors get a way back into their workspace instead of "Log in".
  useEffect(() => {
    fetch("/api/auth/status", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSignedIn(!!(d && (d.logged_in || d.authenticated || d.user))))
      .catch(() => undefined);
  }, []);

  const hoverOpen = (label: string) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const hoverClose = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  const renderGroup = (g: NavGroup) =>
    g.href ? (
      <SmartLink key={g.label} href={g.href} className="cm-btn-ghost h-9 px-3 text-[14px]">
        {g.label}
      </SmartLink>
    ) : (
      <div key={g.label} className="relative" onMouseEnter={() => hoverOpen(g.label)} onMouseLeave={hoverClose}>
        <button
          type="button"
          aria-expanded={openMenu === g.label}
          onClick={() => setOpenMenu((m) => (m === g.label ? null : g.label))}
          className="cm-btn-ghost h-9 gap-1 px-3 text-[14px]"
        >
          {g.label}
          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${openMenu === g.label ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence>
          {openMenu === g.label && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 top-full z-50 pt-2"
            >
              <div className="flex gap-4 rounded-xl border border-border bg-popover p-3 shadow-[var(--shadow-card)]">
                {g.columns!.map((c) => (
                  <MenuColumn key={c.heading} heading={c.heading} links={c.links} onNavigate={() => setOpenMenu(null)} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );

  return (
    <header
      className={`sticky top-0 z-40 w-full bg-background/95 backdrop-blur-sm transition-[border-color] duration-200 ${
        scrolled || mobileOpen ? "border-b border-border" : "border-b border-transparent"
      }`}
    >
      <div className="cm-container flex h-14 items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" aria-label="ClearMakes home" className="flex items-center">
            <Logo />
          </Link>
          <nav className="hidden items-center lg:flex" aria-label="Main">
            {mainNav.map(renderGroup)}
          </nav>
        </div>

        <div className="hidden items-center gap-1.5 lg:flex">
          {!signedIn && <a href={LOGIN_URL} className="cm-btn-ghost h-9 px-3 text-[14px]">Log in</a>}
          <SmartLink href={REQUEST_DEMO_URL} className="cm-btn-secondary h-9 px-3 text-[14px]">Request a demo</SmartLink>
          {signedIn ? (
            <OpenAppMenu />
          ) : (
            <SmartLink href={GET_STARTED_URL} className="cm-btn-primary h-9 px-3 text-[14px]">Get started</SmartLink>
          )}
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <SmartLink href={GET_STARTED_URL} className="cm-btn-primary h-9 px-3 text-[14px]">Get started</SmartLink>
          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className="cm-btn-ghost h-9 w-9 px-0"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-x-0 top-full z-40 h-[calc(100dvh-3.5rem)] overflow-y-auto border-t border-border bg-background lg:hidden"
          >
            <div className="cm-container pb-10 pt-2">
              {mainNav.map((g) =>
                g.href ? (
                  <SmartLink key={g.label} href={g.href} className="flex h-12 items-center border-b border-border text-[17px] font-medium">
                    {g.label}
                  </SmartLink>
                ) : (
                  <div key={g.label} className="border-b border-border">
                    <button
                      type="button"
                      onClick={() => setMobileGroup((m) => (m === g.label ? null : g.label))}
                      className="flex h-12 w-full items-center justify-between text-[17px] font-medium"
                      aria-expanded={mobileGroup === g.label}
                    >
                      {g.label}
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${mobileGroup === g.label ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {mobileGroup === g.label && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-4 pb-4">
                            {g.columns!.map((c) => (
                              <div key={c.heading}>
                                <p className="pb-1 text-xs font-medium text-muted-foreground">{c.heading}</p>
                                {c.links.map((l) => (
                                  <SmartLink key={l.href + l.label} href={l.href} className="block py-1.5 text-[15px] text-foreground">
                                    {l.label}
                                  </SmartLink>
                                ))}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ),
              )}
              <div className="mt-6 grid gap-2">
                <SmartLink href={GET_STARTED_URL} className="cm-btn-primary cm-btn-lg w-full">Get started</SmartLink>
                <SmartLink href={REQUEST_DEMO_URL} className="cm-btn-secondary cm-btn-lg w-full">Request a demo</SmartLink>
                {signedIn ? (
                  liveProducts.map((p) => (
                    <a key={p.slug} href={p.appUrl} className="cm-btn-ghost cm-btn-lg w-full gap-2">
                      <img src={p.logo} alt="" width={20} height={20} className="h-5 w-5" />
                      Open {p.name.replace("ClearMakes ", "")}
                    </a>
                  ))
                ) : (
                  <a href={LOGIN_URL} className="cm-btn-ghost cm-btn-lg w-full">Log in</a>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
